import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap"
import { internalApiClient } from "../src/libs/axios/internalApiClient";
import { slideDown, slideUp } from "../src/slide";

// Connects to data-controller="job-form-step1"
export default class extends Controller {
  static values = {
    jobKey: String,
    jobVersion: Number
  }
  declare readonly jobKeyValue: string;
  declare readonly jobVersionValue: number;

  static targets = ["appointment", "workHoursDetail", "workPlaceBlock", "remoteWorkDetail", "reLocateDetail", "hirePostLocationDetail"];
  declare readonly appointmentTarget: HTMLElement; // 正社員登用の有無
  declare readonly workHoursDetailTarget: HTMLElement; // 勤務時間の詳細
  declare readonly workPlaceBlockTarget: HTMLElement; // 勤務地
  declare readonly remoteWorkDetailTarget: HTMLElement; // リモートワークの詳細
  declare readonly reLocateDetailTarget: HTMLElement; // 転勤の詳細
  declare readonly hirePostLocationDetailTarget: HTMLElement; // 実際の勤務地は採用後に決まりますか？の詳細
  declare deleteTargetSection: HTMLElement; // 削除対象の勤務地セクション

  connect(): void {
    this.initScroll();
    this.initEmploymentStatus();
    this.initWorkHoursDetail();
  }

  initScroll(): void {
    const errorElement = document.querySelector('.txt_error');
    if (errorElement) {
      const elementRect = errorElement.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 2);
      window.scrollTo({ top: middle, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }

  // 雇用形態ラジオボタン選択時の処理（正社員登用の有無の表示切り替え）
  changeEmploymentStatus(eventOrElement: Event | HTMLSelectElement): void {
    const employmentStatus = eventOrElement instanceof Event
      ? (eventOrElement.target as HTMLSelectElement).value
      : eventOrElement.value;

    if (employmentStatus === 'contract_employee' || employmentStatus === 'part_time') {
      if (this.appointmentTarget.classList.contains("d-none")) {
        slideDown(this.appointmentTarget);
      }
    } else {
      if (!this.appointmentTarget.classList.contains("d-none")) {
        slideUp(this.appointmentTarget);
      }
    }
  }

  // 初期表示時の雇用形態選択時の処理（正社員登用の有無の表示切り替え）
  initEmploymentStatus(): void {
    const employmentStatus = document.querySelector<HTMLSelectElement>('input[name="job_content[employment_status]"]:checked');
    if (employmentStatus) {
      this.changeEmploymentStatus(employmentStatus);
    }
  }

  // 勤務時間選択時の処理（詳細のセクションの表示切り替え）
  changeWorkHoursType(eventOrElement: Event | HTMLInputElement): void {
    const workHoursType = eventOrElement instanceof Event
      ? (eventOrElement.target as HTMLSelectElement).value
      : eventOrElement.value;

    // 全てのセクションを取得し、選択した勤務時間に該当するセクションのみを表示
    const sections = this.workHoursDetailTarget.querySelectorAll("section");
    sections.forEach(section => {
      if (section.dataset.value === workHoursType) {
        section.classList.add("is_active");
      } else {
        section.classList.remove("is_active");
      }
    });
    // バリデーションエラーの有無に関係なく、ラジオボタンを１度でも選択するとエラーメッセージのエリアを表示可能にする
    document.getElementById("work_time_notes_error_message")?.classList.remove("d-none");
  }

  // 初期表示時の勤務時間選択時の処理（詳細のセクションの表示切り替え）
  initWorkHoursDetail(): void {
    const workHoursType = document.querySelector<HTMLInputElement>('input[name="job_content[work_time_type]"]:checked');
    if (workHoursType) {
      this.changeWorkHoursType(workHoursType);
    }
  }

  // 勤務時間別の各テキストエリア テンプレ挿入ボタンクリック時の処理
  clickTemplateButton({ params: { id } }): void {
    // パラメータで指定されたテキストエリアを取得
    const workTimeNotesTextarea = document.querySelector(`textarea[name="job_content[${id}]"]`) as HTMLTextAreaElement;
    // プレースホルダーの値を取得して先頭の「例：\n」を削除、テキストエリアに設定
    const placeholder = workTimeNotesTextarea
      .placeholder
      .replace(/^例：/, '')
      .trimStart();

    const currentText = workTimeNotesTextarea.value.trim();
    if (currentText) {
      workTimeNotesTextarea.value = currentText + '\n\n' + placeholder;
    } else {
      workTimeNotesTextarea.value = placeholder;
    }
    workTimeNotesTextarea.focus();
  }

  // 勤務地（出社）の選択時の処理
  changeWorkPlace(eventOrElement: Event | HTMLInputElement): void {
    const isOfficeWork = eventOrElement instanceof Event
      ? (eventOrElement.target as HTMLSelectElement).value
      : eventOrElement.value;

    if (isOfficeWork === 'available') {
      slideDown(this.workPlaceBlockTarget);
    } else {
      slideUp(this.workPlaceBlockTarget);
    }
  }

  // 勤務地の場所追加ボタンのクリック時の処理 | 場所セクションの追加
  addWorkPlace(): void {
    // 最初の .js_work_place_block を取得してクローンを作成
    const originalBlock = this.workPlaceBlockTarget.querySelector('.js_work_place_block') as HTMLElement;

    // 入力フィールドをリセット
    const newBlock = originalBlock.cloneNode(true) as HTMLElement;
    const inputFields = newBlock.querySelectorAll('input');
    inputFields.forEach(input => {
      input.value = '';
    });
    // 都道府県のセレクトボックスをリセット
    const prefectureSelect = newBlock.querySelector('.js_work_place_prefecture') as HTMLSelectElement;
    prefectureSelect.value = '';

    // 市区町村のセレクトボックスの選択肢を "市区町村を選択" にする
    const citySelect = newBlock.querySelector('.js_work_place_city') as HTMLSelectElement;
    citySelect.innerHTML = '<option value="">市区町村を選択</option>';

    // 新しいブロックを追加ボタンの前の兄弟要素として挿入
    const addButton = this.workPlaceBlockTarget.querySelector('.btn_add') as HTMLButtonElement;
    addButton.parentNode?.insertBefore(newBlock, addButton);
  }

  // 勤務地の場所セクション 削除リンククリック時の処理 | 場所セクションの削除
  deleteWorkPlaceBlock(eventOrElement: Event | HTMLButtonElement): void {
    const deleteButton = eventOrElement instanceof Event
      ? (eventOrElement.target as HTMLButtonElement)
      : eventOrElement;

    // 削除対象のセクションを取得
    this.deleteTargetSection = deleteButton.closest('.js_work_place_block') as HTMLElement;

    let placeName = (this.deleteTargetSection.querySelector('.js_work_place_name') as HTMLInputElement).value.trim() || '未入力';
    let prefecture = (this.deleteTargetSection.querySelector('.js_work_place_prefecture option:checked') as HTMLOptionElement).textContent?.trim() || '';
    let city = (this.deleteTargetSection.querySelector('.js_work_place_city option:checked') as HTMLOptionElement).textContent?.trim() || '';
    let address = (prefecture !== '都道府県を選択' ? prefecture : '') + (city !== '市区町村' ? city : '');
    let address_line = (this.deleteTargetSection.querySelector('.js_work_place_address_line') as HTMLInputElement).value.trim() || '';
    let access = (this.deleteTargetSection.querySelector('.js_work_place_access') as HTMLInputElement).value.trim() || '未入力';

    let workPlaceText = `【${placeName}】${address}${address_line}`;
    let accessText = access;

    // モーダルに表示
    const deleteWorkPlaceElement = document.querySelector('.js_delete_work_place') as HTMLElement;
    deleteWorkPlaceElement.innerHTML = `<p>${workPlaceText}</p><p>${accessText}</p>`;
  }

  // 勤務地削除モーダル内の「削除する」ボタンクリック時の処理
  confirmDeleteWorkPlace(eventOrElement: Event | HTMLButtonElement): void {
    // 削除モーダルを開いた際に取得した削除対象セクション
    if (this.deleteTargetSection) {
      const parentContainer = this.deleteTargetSection.parentElement as HTMLElement;
      const sectionCount = parentContainer.querySelectorAll('.js_work_place_block').length;

      // job_work_place.id があれば取得して削除 API へリクエスト
      const jobWorkPlaceId = this.deleteTargetSection.querySelector('input[type="hidden"]') as HTMLInputElement;
      if (jobWorkPlaceId.value) {
        this.sendDeleteWorkPlaceRequest(jobWorkPlaceId.value);
      }

      if (sectionCount > 1) {
        // 2つ以上なら削除
        this.deleteTargetSection.remove();
      } else {
        // 1つしかない場合はフォームをリセット
        const inputFields = this.deleteTargetSection.querySelectorAll('input[type="text"]');
        inputFields.forEach(input => (input as HTMLInputElement).value = '');

        const selectFields = this.deleteTargetSection.querySelectorAll('select');
        selectFields.forEach(select => (select as HTMLSelectElement).selectedIndex = 0);

        // job_work_place.id のhiddenフィールドを削除
        jobWorkPlaceId.value = '';
      }
    }

    // モーダルを閉じる
    const modal = document.getElementById('deleteWorkPlaceModal') as HTMLElement;
    if (modal) {
      Modal.getOrCreateInstance(modal).hide();
    }
  }

  // 勤務地の削除APIリクエスト
  async sendDeleteWorkPlaceRequest(jobWorkPlaceId: string): Promise<void> {
    const response = await internalApiClient.delete(`/api/internal/v1/job_work_places/${jobWorkPlaceId}`, {
      params: {
        job_key: this.jobKeyValue,
        version: this.jobVersionValue
      }
    });
  }

  // リモートワークの選択時の処理
  changeRemoteWork({ params }): void {
    const isRemoteWork = params.isRemoteWork;
    if (isRemoteWork) {
      slideDown(this.remoteWorkDetailTarget);
    } else {
      slideUp(this.remoteWorkDetailTarget);
    }
  }

  // 転勤の選択時の処理
  changeReLocate({ params }): void {
    const isReLocate = params.isReLocate;
    if (isReLocate) {
      slideDown(this.reLocateDetailTarget);
    } else {
      slideUp(this.reLocateDetailTarget);
    }
  }

  // 実際の勤務地は採用後に決まりますか？の選択時の処理
  changeHirePostLocation({ params }): void {
    const isHirePostLocation = params.isHirePostLocation;
    if (isHirePostLocation) {
      slideDown(this.hirePostLocationDetailTarget);
    } else {
      slideUp(this.hirePostLocationDetailTarget);
    }
  }
}
